import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { Page } from "@/components/Shell";
import { ConfirmAction } from "@/components/ConfirmAction";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const Row = ({ m, onSaved, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(m.content);
  const save = async () => {
    try { await api.patch(`/memories/${m.id}`, { content: val }); setEdit(false); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li className="group border-b border-[#1c1c1c] py-6 flex gap-6 items-start" data-testid={`memory-item-${m.id}`}>
      <div className="flex-1 min-w-0">
        {edit ? (
          <textarea value={val} onChange={(e) => setVal(e.target.value)} data-testid={`memory-edit-input-${m.id}`} rows={2}
            className="w-full bg-transparent border-b border-[#D8BFD8]/50 outline-none text-ivory py-2 resize-none" />
        ) : <p className="font-display text-xl md:text-2xl text-ivory leading-snug" data-testid={`memory-content-${m.id}`}>{m.content}</p>}
        <p className="mt-2 text-xs text-[#5f5f5f]">{m.source === "conversation" ? "Saved from conversation" : "Added manually"} · {fmtTime(m.updated_at || m.created_at)}</p>
      </div>
      <div className="flex gap-4 pt-2 md:opacity-40 group-hover:opacity-100 transition-opacity">
        {edit ? <>
          <button onClick={save} data-testid={`memory-save-${m.id}`} aria-label="Save"><Check className="h-4 w-4 text-[#C0C0C0]" strokeWidth={1.5} /></button>
          <button onClick={() => { setEdit(false); setVal(m.content); }} aria-label="Cancel edit" data-testid={`memory-cancel-${m.id}`}><X className="h-4 w-4 text-[#8A8A8A]" strokeWidth={1.5} /></button>
        </> : <button onClick={() => setEdit(true)} data-testid={`memory-edit-${m.id}`} aria-label="Edit"><Pencil className="h-4 w-4 text-[#8A8A8A] hover:text-ivory" strokeWidth={1.5} /></button>}
        <button onClick={() => onDelete(m.id)} data-testid={`memory-delete-${m.id}`} aria-label="Delete"><Trash2 className="h-4 w-4 text-[#8A8A8A] hover:text-[#e2a6a6]" strokeWidth={1.5} /></button>
      </div>
    </li>
  );
};

export default function MemoryPage() {
  const { user, reload } = useAuth();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const load = () => api.get("/memories").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post("/memories", { content: text }); setText(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { try { await api.delete(`/memories/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const wipe = async () => {
    try { await api.delete("/memories"); load(); toast("All memories deleted."); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (v) => { try { await api.patch("/settings", { memory_enabled: v }); reload(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <Page eyebrow="What Feerd keeps" title="Memory" intro="Persistent memory is separate from conversation context. Feerd stores something only when you add it here or explicitly ask it to remember. Nothing is collected silently."
      action={<label className="flex items-center gap-4 text-xs tracking-[0.18em] uppercase text-[#8A8A8A]">Memory {user?.settings?.memory_enabled !== false ? "on" : "off"}
        <Switch checked={user?.settings?.memory_enabled !== false} onCheckedChange={toggle} data-testid="memory-enabled-switch" /></label>}>
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-4 mb-14 max-w-3xl" data-testid="memory-add-form">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. I prefer vegetarian restaurants" data-testid="memory-add-input"
          className="flex-1 bg-transparent border-b border-[#2A2A2A] focus:border-[#D8BFD8] outline-none py-3 text-ivory placeholder:text-[#4a4a4a] transition-colors" />
        <button data-testid="memory-add-button" className="px-6 py-3 rounded-full bg-[#C0C0C0] text-[#0A0A0A] text-xs tracking-[0.2em] uppercase hover:bg-[#F8F4E6] transition-colors">Remember</button>
      </form>
      {items && items.length === 0 && <p className="font-display italic text-2xl text-[#5f5f5f]" data-testid="memory-empty">Feerd remembers nothing about you yet.</p>}
      <ul className="max-w-4xl border-t border-[#1c1c1c]" data-testid="memory-list">
        {(items || []).map((m) => <Row key={m.id} m={m} onSaved={load} onDelete={del} />)}
      </ul>
      {items?.length > 0 && <ConfirmAction title="Forget everything?" description="Every stored memory will be permanently deleted." confirmLabel="Forget all" onConfirm={wipe} testId="memory-delete-all-dialog"
        trigger={<button data-testid="memory-delete-all" className="mt-12 text-xs tracking-[0.2em] uppercase text-[#8A8A8A] hover:text-[#e2a6a6] transition-colors">Forget everything</button>} />}
    </Page>
  );
}
